import React, { useState, useRef } from 'react';
import { api } from '../../lib/api';
import { Upload, Sparkles, Loader2, FileText, AlertTriangle, CheckCircle2, X, Building2, Layers } from 'lucide-react';
import { toast } from 'sonner';
import PEBTonnageCalc from './PEBTonnageCalc';
import AIJobProgress from '../../components/AIJobProgress';

const ACCEPT_EXTS = '.pdf,.xls,.xlsx,.dxf,.dwg,.zip';
const MAX_TOTAL_MB = 80;
const MAX_FILES = 12;

export default function TonnageCalcAI() {
  const [files, setFiles] = useState([]);          // multiple files
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);      // { sheds[], grand_total, sources, skipped_dwg }
  const [error, setError] = useState(null);
  const [activeJob, setActiveJob] = useState(null); // { id } — async extraction job being polled
  const [activeShed, setActiveShed] = useState(0);
  const inputRef = useRef(null);

  const addFiles = (fl) => {
    setError(null);
    if (!fl || fl.length === 0) return;
    const incoming = Array.from(fl);
    const merged = [...files];
    for (const f of incoming) {
      if (merged.find(m => m.name === f.name && m.size === f.size)) continue;
      merged.push(f);
    }
    if (merged.length > MAX_FILES) {
      setError(`Max ${MAX_FILES} files. Remove some before adding more.`);
      return;
    }
    const total = merged.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_MB * 1024 * 1024) {
      setError(`Total upload would be ${(total/1024/1024).toFixed(1)} MB — exceeds ${MAX_TOTAL_MB} MB cap.`);
      return;
    }
    setFiles(merged);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const runExtraction = async () => {
    if (files.length === 0) { toast.error('Pick at least one tender file first'); return; }
    setLoading(true); setError(null); setResult(null); setActiveShed(0); setActiveJob(null);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      // Async job: the upload returns a job_id immediately and the heavy
      // OCR + Claude extraction runs in the background (scanned PDFs can take
      // minutes — far longer than the ingress request timeout, which used to
      // surface as a 502). We then poll /api/jobs/{id} via <AIJobProgress />.
      const r = await api.post('/peb-tonnage/extract-multi', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      if (r.data?.job_id) {
        setActiveJob({ id: r.data.job_id });
      } else {
        // Backwards-compat: server returned the result inline
        setResult(r.data);
        setLoading(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Extraction failed';
      setError(typeof msg === 'string' ? msg : 'AI extraction failed — try clearer tender docs');
      setLoading(false);
    }
  };

  const onJobComplete = (jobResult) => {
    setActiveJob(null);
    setLoading(false);
    setResult(jobResult);
    const n = jobResult?.grand_total?.shed_count || 0;
    toast.success(`AI detected ${n} shed${n === 1 ? '' : 's'} · grand total ${jobResult?.grand_total?.total_tonnes?.toFixed(2)} T`);
  };

  const onJobFail = (err) => {
    setActiveJob(null);
    setLoading(false);
    setError(typeof err === 'string' ? err : 'AI extraction failed — try clearer tender docs');
  };

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="tonnage-ai-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#3b4872] mb-1.5 flex items-center gap-1.5">
          <Sparkles size={12} /> Experimental · AI-Powered · Multi-File · Multi-Shed
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">Tonnage Calculator · AI</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Drop in PEB tender PDFs, Excel spec sheets, DXF drawing exports, or a zip — Ellora AI reads everything,
          detects how many sheds the project has, and produces a steel tonnage breakdown per shed plus a grand total.
          DWG files need a DXF export to be parsed.
        </p>
      </div>

      {/* Upload card */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        {/* Format guidance banner — PDF/XLS give best AI extraction; DWG is binary and skipped */}
        <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 px-3 py-2.5 rounded-sm text-xs text-amber-900 flex items-start gap-2" data-testid="tonnage-format-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <strong className="font-display font-bold">For best results, upload <u>PDF spec sheets or Excel BOQs</u>.</strong>
            {' '}Scanned / image-only PDFs are now auto-OCR'd (slower · ~5-10s per page).
            DWG drawings are binary and <strong>cannot be parsed</strong> — they'll be skipped with a warning.
            If you only have DWG, please export it from AutoCAD as <strong>DXF (R2018)</strong> first, or attach the architect's PDF spec sheet alongside.
          </div>
        </div>

        <div
          className="border-2 border-dashed border-slate-300 hover:border-[#3b4872] hover:bg-slate-50 rounded-sm p-8 text-center cursor-pointer transition-colors"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          data-testid="tonnage-ai-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_EXTS}
            onChange={(e) => addFiles(e.target.files)}
            className="hidden"
            data-testid="tonnage-ai-input"
          />
          <Upload className="mx-auto text-slate-400" size={28} />
          <div className="font-display font-bold text-sm mt-1">Drag tender files here, or click to browse</div>
          <div className="text-xs text-slate-500 mt-1">.pdf · .xls · .xlsx · .dxf · .dwg · .zip — up to {MAX_FILES} files / {MAX_TOTAL_MB} MB total</div>
        </div>

        {files.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="tonnage-ai-file-list">
            <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              {files.length} file{files.length === 1 ? '' : 's'} · {(files.reduce((s,f)=>s+f.size,0)/1024/1024).toFixed(2)} MB
            </div>
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5">
                <FileText size={12} className="text-[#3b4872] shrink-0" />
                <span className="font-mono truncate flex-1">{f.name}</span>
                <span className="text-slate-400 tabular-nums">{(f.size/1024/1024).toFixed(2)} MB</span>
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-600" data-testid={`tonnage-file-remove-${i}`}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <Sparkles size={11} /> Powered by Ellora AI
          </div>
          <button
            onClick={runExtraction}
            disabled={files.length === 0 || loading}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-display font-semibold bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="tonnage-ai-run-btn"
          >
            {loading ? (<><Loader2 size={14} className="animate-spin" /> Extracting…</>) : (<><Sparkles size={14} /> Extract & Compute</>)}
          </button>
        </div>

        {error && (
          <div className="mt-4 bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2 rounded-sm flex items-start gap-2" data-testid="tonnage-ai-error">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {activeJob && (
          <div className="mt-4" data-testid="tonnage-ai-job">
            <AIJobProgress
              jobId={activeJob.id}
              title="Reading tender & computing tonnage…"
              onComplete={onJobComplete}
              onFail={onJobFail}
              onCancel={() => { setActiveJob(null); setLoading(false); }}
            />
          </div>
        )}
      </div>

      {/* Result — Grand Total + per-shed tabs */}
      {result && (
        <>
          {/* Grand total banner */}
          <div className="bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm p-5" data-testid="tonnage-grand-total">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-amber-300 mb-1">Grand Total · {result.grand_total.shed_count} shed{result.grand_total.shed_count === 1 ? '' : 's'}</div>
                <div className="font-display font-extrabold text-3xl tabular-nums">{result.grand_total.total_tonnes.toFixed(2)} T</div>
                {result.project_name && <div className="text-sm text-slate-300 mt-1">{result.project_name}{result.client ? ` · ${result.client}` : ''}{result.location ? ` · ${result.location}` : ''}</div>}
              </div>
              <div className="grid grid-cols-3 gap-4 text-right">
                <Stat label="Steel only" value={`${result.grand_total.steel_only_tonnes.toFixed(2)} T`} />
                <Stat label="Sheeting" value={`${result.grand_total.sheeting_tonnes.toFixed(2)} T`} />
                <Stat label="Built-up" value={`${result.grand_total.built_up_sqm.toFixed(0)} sqm`} />
              </div>
            </div>
            {result.skipped_dwg?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-amber-200 flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <div>DWG files cannot be parsed in this environment: <span className="font-mono">{result.skipped_dwg.join(', ')}</span>. Re-export them as DXF (AutoCAD → Save As → DXF R2018) for accurate dimensions.</div>
              </div>
            )}
            {result.grand_total.any_partial && (
              <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-amber-200 flex items-start gap-2" data-testid="any-partial-warning">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <div>
                  <strong className="font-display font-bold">Grand total is partial.</strong>{' '}
                  {result.grand_total.pending_count > 0
                    ? <>{result.grand_total.pending_count} shed{result.grand_total.pending_count === 1 ? '' : 's'} had no dimensions in the document — they are <strong>excluded</strong> from the total. Open each tab and enter the dimensions manually to include them.</>
                    : <>Some sheds have missing non-critical fields. Open each tab to verify before sending a quote.</>}
                </div>
              </div>
            )}
            {result.ocr_used_files?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-sky-200 flex items-start gap-2" data-testid="ocr-used-banner">
                <Sparkles size={13} className="mt-0.5 shrink-0" />
                <div>OCR was used to read scanned / image-only PDF{result.ocr_used_files.length === 1 ? '' : 's'}: <span className="font-mono">{result.ocr_used_files.join(', ')}</span>. Accuracy depends on scan quality — please verify extracted dimensions before quoting.</div>
              </div>
            )}
            {result.sources?.length > 0 && (
              <div className="mt-2 text-[10px] text-slate-400 font-mono">Sources: {result.sources.join(' · ')}</div>
            )}
          </div>

          {/* Per-shed tabs */}
          {result.sheds.length > 1 && (
            <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="shed-tabs">
              {result.sheds.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActiveShed(i)}
                  data-testid={`shed-tab-${i}`}
                  className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-widest border-b-2 whitespace-nowrap flex items-center gap-1.5 ${activeShed === i ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                  <Building2 size={12} /> {s.label}
                  {s.tonnage
                    ? <span className="text-slate-400 tabular-nums">· {s.tonnage.total_tonnes.toFixed(1)} T</span>
                    : <span className="text-slate-400">· —</span>}
                  {s.extraction_status === 'needs_input' && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700 text-[9px]">NEEDS INPUT</span>}
                  {s.extraction_status === 'partial' && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 text-[9px]">PARTIAL</span>}
                </button>
              ))}
            </div>
          )}

          {/* Active shed */}
          {result.sheds[activeShed] && (
            <ShedCard shed={result.sheds[activeShed]} idx={activeShed} sourcesSummary={result.sources?.join(', ')} />
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display font-bold text-base tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function ShedCard({ shed, idx, sourcesSummary }) {
  const status = shed.extraction_status || 'complete';
  const missing = shed.missing_fields || [];
  const critical = shed.critical_missing || [];
  const ex = shed.extracted || shed.spec || {};
  const niceField = (f) => ({
    length: 'Length', width: 'Width', eave_height: 'Eave Height', clear_span: 'Clear Span',
    bay_spacing: 'Bay Spacing', roof_slope_deg: 'Roof Slope', wind_speed: 'Wind Speed',
    seismic_zone: 'Seismic Zone', eot_crane_capacity: 'EOT Crane', steel_grade_mpa: 'Steel Grade',
    design_codes: 'Design Codes',
  }[f] || f);

  return (
    <div className="space-y-4" data-testid={`shed-card-${idx}`}>
      {/* Extraction status banner */}
      {status === 'needs_input' && (
        <div className="bg-slate-50 border-l-4 border-slate-400 rounded-sm p-4" data-testid={`shed-status-${idx}`}>
          <div className="flex items-start gap-2 text-sm text-slate-800">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-slate-500" />
            <div className="space-y-1 flex-1">
              <div className="font-display font-bold">No dimensions found in the document for {shed.label}</div>
              <div className="text-xs">
                The AI did <strong>not</strong> find {critical.map(niceField).join(', ') || 'the building dimensions'} in the uploaded files, so nothing has been assumed.
                Any other details it did read are pre-filled below. <strong>Enter the dimensions manually</strong> to compute tonnage for this shed.
              </div>
            </div>
          </div>
        </div>
      )}
      {status === 'partial' && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-sm p-4" data-testid={`shed-status-${idx}`}>
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
            <div className="space-y-1 flex-1">
              <div className="font-display font-bold">Some non-critical fields were not in the document for {shed.label}</div>
              <div className="text-xs">
                Not found (left blank or at standard values): <strong className="font-mono">{missing.map(niceField).join(', ')}</strong>.
                {' '}The dimensions were read OK, so tonnage is computed — but please confirm the blank fields before quoting.
              </div>
            </div>
          </div>
        </div>
      )}
      {status === 'complete' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
          <div className="flex items-start gap-2 text-sm text-emerald-900">
            <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
            <div className="space-y-1 flex-1">
              <div className="font-display font-bold flex items-center gap-2">
                <Layers size={13} /> {shed.label}
                <span className="text-xs font-normal text-emerald-700">· {shed.tonnage ? `${shed.tonnage.total_tonnes.toFixed(2)} T total · ` : ''}all dimensions extracted</span>
              </div>
              {ex.end_use && <div className="text-xs">End-use: <strong>{ex.end_use}</strong></div>}
              {ex.notes && <div className="text-xs text-emerald-700/80">{ex.notes}</div>}
              <div className="text-xs">Codes detected: {(ex.design_codes || []).join(' · ') || '—'}</div>
            </div>
          </div>
        </div>
      )}
      <PEBTonnageCalc initialSpec={shed.extracted} source={sourcesSummary || `Shed ${idx + 1}`} key={`shed-calc-${idx}`} />
    </div>
  );
}
